import { Button, IconButton, Typography } from 'omakase';
import { Grid } from 'ui';
import { Head } from 'head/Head';
import { IconEllipses, IconMegaphone, IconShare } from '@tock/icons';
import { consumer } from '@tock/coretypes';
import { getPlaylistType } from 'utils/profileUtils';
import { makeStyles } from '@material-ui/core/styles';
import PlaylistTypeIcon from 'components/profile/playlists/PlaylistTypeIcon';

const getPlaylistSubtext = (publicPage: boolean | undefined, playlist: consumer.Playlist) => {
  let subtitle;
  if (publicPage) {
    subtitle = `${playlist.creator?.firstName} ${playlist.creator?.lastName}`;
  } else if (playlist.isPublic) {
    subtitle = 'Public';
  } else {
    subtitle = 'Private';
  }
  return `${subtitle}  •  ${playlist.items.length} ${playlist.items.length > 1 ? 'places' : 'place'}`;
};

export type PlaylistPermissionsMode = 'PUBLIC' | 'PRIVATE';

interface PlaylistPageTitleProps {
  playlist: consumer.Playlist;
  onEdit(): void;
  publicPage?: boolean;
  onCopyLink(): void;
  onChangePermissions(mode: PlaylistPermissionsMode): void;
}

const usePlaylistPageTitleStyles = makeStyles((theme) => ({
  desktopTitle: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'inherit',
    },
  },
  mobileTitle: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
}));

const ProfilePlaylistDetailsTitle: React.FunctionComponent<PlaylistPageTitleProps> = ({
  playlist,
  onEdit,
  publicPage,
  onCopyLink,
  onChangePermissions,
}) => {
  const styles = usePlaylistPageTitleStyles();
  return (
    <>
      <div className={styles.desktopTitle}>
        <ProfilePlaylistDetailsTitleDesktop
          playlist={playlist}
          publicPage={publicPage}
          onEdit={onEdit}
          onCopyLink={onCopyLink}
        />
      </div>
      <div className={styles.mobileTitle}>
        <ProfilePlaylistDetailsTitleMobile
          playlist={playlist}
          publicPage={publicPage}
          onEdit={onEdit}
          onCopyLink={onCopyLink}
          onChangePermissions={onChangePermissions}
        />
      </div>
    </>
  );
};

export default ProfilePlaylistDetailsTitle;

const usePlaylistPageTitleDesktopStyles = makeStyles((theme) => ({
  iconAndTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  actionButton: {
    height: theme.spacing(2),
    width: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  description: {
    marginTop: theme.spacing(0.5),
  },
  shareLink: {
    borderBottom: `1px solid ${theme.colors.borderPrimaryBase}`,
    cursor: 'pointer',
    marginTop: theme.spacing(2),
  },
}));

type TitleProps = PlaylistPageTitleProps;

const ProfilePlaylistDetailsTitleDesktop: React.FunctionComponent<
  Omit<TitleProps, 'onChangePermissions'>
> = ({ playlist, publicPage, onEdit, onCopyLink }) => {
  const styles = usePlaylistPageTitleDesktopStyles();
  return (
    <>
      <Head title={playlist.name} />
      <div className={styles.titleContainer}>
        <div className={styles.iconAndTitle}>
          <div>
            <Typography component="h2" variant="heading02">
              {playlist.name}
            </Typography>
            {playlist.description && (
              <Typography variant="body02">{playlist.description}</Typography>
            )}
            <Typography variant="body02" color="fgSubtle">
              {getPlaylistSubtext(publicPage, playlist)}
            </Typography>
            {publicPage ? (
              <Typography
                className={styles.shareLink}
                variant="overline"
                color="fgPrimaryBase"
                component="button"
                onClick={onCopyLink}
              >
                Share it!
              </Typography>
            ) : null}
          </div>
        </div>
        <div>
          <Grid container spacing={1}>
            {/* todo this icon is for when we can add businesses to the list via a search function */}
            {/* {!publicPage && (
            <Grid item>
              <IconButton variant="outlined" color="primary">
                <SvgIconPlus className={styles.actionButton} />
              </IconButton>
            </Grid>
          )} */}
            {!publicPage ? (
              <>
                {playlist.isPublic && (
                  <Grid item>
                    <IconButton
                      variant="basic"
                      size="medium"
                      onClick={onCopyLink}
                      aria-label="Share list"
                    >
                      <IconShare className={styles.actionButton} />
                    </IconButton>
                  </Grid>
                )}
                <Grid item>
                  <IconButton
                    onClick={onEdit}
                    variant="basic"
                    size="medium"
                    aria-label="List options"
                  >
                    <IconEllipses className={styles.actionButton} />
                  </IconButton>
                </Grid>
              </>
            ) : null}
          </Grid>
        </div>
      </div>
    </>
  );
};

const usePlaylistPageTitleMobileStyles = makeStyles((theme) => ({
  iconAndTitle: {
    display: 'flex',
  },
  actionsMobile: {
    marginTop: theme.spacing(1),
  },
  actionMobileSpacing: {
    marginRight: theme.spacing(1),
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  centerContent: {
    display: 'flex',
    justifyContent: 'center',
  },
  playlistIcon: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
}));

const ProfilePlaylistDetailsTitleMobile: React.FunctionComponent<TitleProps> = ({
  playlist,
  publicPage,
  onEdit,
  onCopyLink,
  onChangePermissions,
}) => {
  const styles = usePlaylistPageTitleMobileStyles();

  return (
    <div className={styles.titleContainer}>
      <div className={styles.iconAndTitle}>
        <Grid container>
          <Grid item xs={12} className={styles.centerContent}>
            <PlaylistTypeIcon
              className={styles.playlistIcon}
              playlistType={getPlaylistType(playlist)}
            />
          </Grid>
          <Grid item xs={12} className={styles.centerContent}>
            <Typography component="h5" variant="heading03">
              {playlist.name}
            </Typography>
          </Grid>
          <Grid item xs={12} className={styles.centerContent}>
            {playlist.description && (
              <Typography variant="body02" color="fgSubtle">
                {playlist.description}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} className={styles.centerContent}>
            <Typography variant="body02" color="fgSubtle">
              {getPlaylistSubtext(publicPage, playlist)}
            </Typography>
          </Grid>
          <Grid item xs={12} className={styles.centerContent}>
            <div className={styles.actionsMobile}>
              {/* {!publicPage && (
                <Button
                  className={styles.actionMobileSpacing}
                  startIcon={<SvgIconPlus />}
                  variant="outlined"
                >
                  Add
                </Button>
              )} */}
              {playlist.isPublic && (
                <Button
                  className={styles.actionMobileSpacing}
                  iconStart={<IconShare />}
                  variant="basic"
                  size="medium"
                  onClick={onCopyLink}
                >
                  Share
                </Button>
              )}
              <Button
                className={styles.actionMobileSpacing}
                variant="basic"
                size="medium"
                onClick={() => onChangePermissions(playlist.isPublic ? 'PRIVATE' : 'PUBLIC')}
                iconStart={<IconMegaphone />}
              >
                {playlist.isPublic ? 'Make private' : 'Make public'}
              </Button>
              {!publicPage && (
                <IconButton
                  variant="basic"
                  size="medium"
                  aria-label="More actions"
                  onClick={onEdit}
                >
                  <IconEllipses />
                </IconButton>
              )}
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};
